import { useState, useEffect } from "react";
import axios from "axios";
import {
  Settings,
  FileType,
  Cloud,
  Check,
  X,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  RefreshCw,
  ExternalLink,
  Info
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Alert, AlertDescription } from "../components/ui/alert";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONVERSION_SERVICES = [
  {
    id: "convertapi",
    name: "ConvertAPI",
    description: "High-quality conversion with 500+ formats. 250 free conversions.",
    signupUrl: "https://www.convertapi.com/a/signup",
    fields: [
      { key: "api_secret", label: "API Secret", placeholder: "Your ConvertAPI secret key" }
    ],
    recommended: true
  },
  {
    id: "cloudconvert",
    name: "CloudConvert",
    description: "Popular converter with 200+ formats. 25 free conversions/day.",
    signupUrl: "https://cloudconvert.com/register",
    fields: [
      { key: "api_key", label: "API Key", placeholder: "Your CloudConvert API key" }
    ]
  },
  {
    id: "cloudmersive",
    name: "Cloudmersive",
    description: "Enterprise-grade API. 600 free conversions/month.",
    signupUrl: "https://account.cloudmersive.com/signup",
    fields: [
      { key: "api_key", label: "API Key", placeholder: "Your Cloudmersive API key" }
    ]
  }
];

export default function ConversionSettings() {
  const [config, setConfig] = useState({
    enabled: false,
    service: "",
    credentials: {}
  });
  const [systemHealth, setSystemHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});

  useEffect(() => {
    fetchConfig();
    fetchSystemHealth();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/conversion-config`);
      setConfig(response.data);
    } catch (error) {
      console.error("Failed to fetch config:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemHealth = async () => {
    try {
      const response = await axios.get(`${API}/system/health`);
      setSystemHealth(response.data);
    } catch (error) {
      console.error("Failed to fetch health:", error);
    }
  };

  const handleServiceChange = (serviceId) => {
    setConfig(prev => ({
      ...prev,
      service: serviceId,
      credentials: {}
    }));
  };

  const handleCredentialChange = (key, value) => {
    setConfig(prev => ({
      ...prev,
      credentials: {
        ...prev.credentials,
        [key]: value
      }
    }));
  };

  const saveConfig = async () => {
    if (!config.service) {
      toast.error("Please select a conversion service");
      return;
    }

    const selectedService = CONVERSION_SERVICES.find(s => s.id === config.service);
    const missingFields = selectedService?.fields.filter(
      f => !config.credentials[f.key]
    );

    if (missingFields?.length > 0) {
      toast.error(`Please fill in: ${missingFields.map(f => f.label).join(", ")}`);
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/admin/conversion-config`, {
        ...config,
        enabled: true
      });
      toast.success("Conversion settings saved successfully!");
      fetchSystemHealth();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    if (!config.service || !config.credentials) {
      toast.error("Please configure a service first");
      return;
    }

    setTesting(true);
    try {
      const response = await axios.post(`${API}/admin/conversion-config/test`, config);
      if (response.data.success) {
        toast.success("Connection successful! Conversion service is working.");
      } else {
        toast.error(response.data.message || "Connection test failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Connection test failed");
    } finally {
      setTesting(false);
    }
  };

  const disableService = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/conversion-config`, {
        enabled: false,
        service: "",
        credentials: {}
      });
      setConfig({ enabled: false, service: "", credentials: {} });
      toast.success("Conversion service disabled");
      fetchSystemHealth();
    } catch (error) {
      toast.error("Failed to disable service");
    } finally {
      setSaving(false);
    }
  };

  const toggleShowSecret = (key) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const selectedService = CONVERSION_SERVICES.find(s => s.id === config.service);
  const libreOfficeAvailable = systemHealth?.checks?.libreoffice?.installed;
  const conversionMethod = systemHealth?.checks?.ppt_conversion_method?.method;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="conversion-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Settings className="w-8 h-8 text-primary" />
          Conversion Settings
        </h1>
        <p className="text-[#5C5C5C] mt-1">
          Configure PPT/PPTX to PDF conversion for presentation uploads
        </p>
      </div>

      {/* Current Status */}
      <Card className="stats-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileType className="w-5 h-5 text-primary" />
            Current Conversion Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="p-4 bg-secondary/50 rounded-xl">
              <p className="text-sm text-[#5C5C5C]">LibreOffice (Local)</p>
              <div className="flex items-center gap-2 mt-1">
                {libreOfficeAvailable ? (
                  <>
                    <Check className="w-5 h-5 text-[#059669]" />
                    <span className="font-semibold text-[#059669]">Available</span>
                  </>
                ) : (
                  <>
                    <X className="w-5 h-5 text-[#DC2626]" />
                    <span className="font-semibold text-[#DC2626]">Not Available</span>
                  </>
                )}
              </div>
            </div>
            
            <div className="p-4 bg-secondary/50 rounded-xl">
              <p className="text-sm text-[#5C5C5C]">Cloud API</p>
              <div className="flex items-center gap-2 mt-1">
                {config.enabled ? (
                  <>
                    <Check className="w-5 h-5 text-[#059669]" />
                    <span className="font-semibold text-[#059669]">
                      {CONVERSION_SERVICES.find(s => s.id === config.service)?.name || "Configured"}
                    </span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-5 h-5 text-[#F59E0B]" />
                    <span className="font-semibold text-[#F59E0B]">Not Configured</span>
                  </>
                )}
              </div>
            </div>
            
            <div className="p-4 bg-primary/10 rounded-xl">
              <p className="text-sm text-[#5C5C5C]">Active Method</p>
              <p className="font-semibold text-primary mt-1">
                {conversionMethod || "None"}
              </p>
            </div>
          </div>

          {!libreOfficeAvailable && !config.enabled && (
            <Alert className="mt-4 border-[#DC2626]/20 bg-[#DC2626]/5">
              <AlertCircle className="h-4 w-4 text-[#DC2626]" />
              <AlertDescription className="text-[#DC2626]">
                <strong>Action Required:</strong> No conversion method available. PPT/PPTX uploads will fail. 
                Please configure a cloud conversion service below.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Service Selection */}
      <Card className="stats-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Cloud className="w-5 h-5 text-primary" />
            Cloud Conversion Service
          </CardTitle>
          <CardDescription>
            Select and configure a cloud-based conversion service for PPT/PPTX files
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Service Dropdown */}
          <div className="space-y-2">
            <Label>Conversion Service</Label>
            <Select value={config.service} onValueChange={handleServiceChange}>
              <SelectTrigger className="w-full" data-testid="service-select">
                <SelectValue placeholder="Select a conversion service" />
              </SelectTrigger>
              <SelectContent>
                {CONVERSION_SERVICES.map(service => (
                  <SelectItem key={service.id} value={service.id}>
                    <div className="flex items-center gap-2">
                      {service.name}
                      {service.recommended && (
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                          Recommended
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Selected Service Details */}
          {selectedService && (
            <div className="space-y-4 p-4 bg-secondary/30 rounded-xl">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-[#1A1A1A]">{selectedService.name}</h3>
                  <p className="text-sm text-[#5C5C5C] mt-1">{selectedService.description}</p>
                </div>
                <a 
                  href={selectedService.signupUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline text-sm flex items-center gap-1"
                >
                  Get API Key <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              {/* Credential Fields */}
              <div className="space-y-4 pt-4 border-t">
                {selectedService.fields.map(field => (
                  <div key={field.key} className="space-y-2">
                    <Label htmlFor={field.key}>{field.label}</Label>
                    <div className="relative">
                      <Input
                        id={field.key}
                        type={showSecrets[field.key] ? "text" : "password"}
                        placeholder={field.placeholder}
                        value={config.credentials[field.key] || ""}
                        onChange={(e) => handleCredentialChange(field.key, e.target.value)}
                        className="pr-10"
                        data-testid={`input-${field.key}`}
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowSecret(field.key)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#5C5C5C]"
                      >
                        {showSecrets[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Info box */}
              <div className="flex items-start gap-2 p-3 bg-primary/5 rounded-lg text-sm">
                <Info className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <div className="text-[#5C5C5C]">
                  <p>Your API key is stored securely and used only for converting PPT/PPTX files to PDF.</p>
                  <p className="mt-1">The conversion happens automatically when users upload presentation files.</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4">
            <Button 
              onClick={saveConfig} 
              disabled={saving || !config.service}
              className="rounded-full"
              data-testid="save-config-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save Configuration
            </Button>
            
            <Button 
              variant="outline" 
              onClick={testConnection}
              disabled={testing || !config.service}
              className="rounded-full"
              data-testid="test-connection-btn"
            >
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Test Connection
            </Button>

            {config.enabled && (
              <Button 
                variant="ghost" 
                onClick={disableService}
                disabled={saving}
                className="rounded-full text-[#DC2626] hover:text-[#DC2626] hover:bg-[#DC2626]/10"
              >
                <X className="w-4 h-4 mr-2" />
                Disable
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="stats-card">
        <CardHeader>
          <CardTitle className="text-lg">How It Works</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-3 text-[#5C5C5C]">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary text-white text-sm flex items-center justify-center flex-shrink-0">1</span>
              <span>User uploads a PPT/PPTX file when creating a session</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary text-white text-sm flex items-center justify-center flex-shrink-0">2</span>
              <span>The file is sent to the configured cloud service for conversion to PDF</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary text-white text-sm flex items-center justify-center flex-shrink-0">3</span>
              <span>Individual slide images are extracted from the PDF</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-primary text-white text-sm flex items-center justify-center flex-shrink-0">4</span>
              <span>Slides are displayed during the presentation session</span>
            </li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
